import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { COMPANY } from "../lib/company";
import { logError } from "../lib/logger";
import { signInWithNext } from "../lib/redirect";
import { useWorkspace } from "../lib/WorkspaceContext";
import { Spinner } from "./Spinner";

/**
 * The web half of "Settings → Delete Account" — the same request the phone
 * makes, through the same RPC (20260911000100).
 *
 * It lives on /data-deletion, a pre-rendered public page that Meta and Google
 * read without JavaScript, so it renders nothing until the session is known.
 * The pre-rendered HTML and the first client render therefore match, the page
 * the reviewers read is unchanged, and nobody signed in sees a sign-in link
 * flash past before their form appears.
 *
 * Recording the request locks the account and revokes its sessions
 * (20260912000400). Sign-out afterwards is only tidying: auth-js clears the
 * local session whatever the server answers.
 */
export function DeleteAccountForm() {
  // undefined = not asked yet; null = signed out.
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const { active } = useWorkspace();
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once the request is recorded; `scheduledFor` is null if the RPC gave no date.
  const [done, setDone] = useState(false);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Profile links to /data-deletion#account. A pushState navigation does not
  // scroll to a hash, and this block is not there to scroll to until now.
  useEffect(() => {
    if (session !== undefined && window.location.hash === "#account") {
      document.getElementById("account")?.scrollIntoView();
    }
  }, [session]);

  async function submit() {
    if (!confirm(
      "Delete your account?\n\nYour request is recorded, you are signed out everywhere, " +
      "and this account can no longer sign in. This cannot be undone in the app.",
    )) return;

    setBusy(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("request_account_deletion", {
      p_reason: reason.trim() || null,
    });
    if (rpcError) {
      setBusy(false);
      void logError("auth", rpcError, { step: "request_account_deletion" });
      setError(
        `Could not submit the request. Check your connection and try again. ` +
        `If it keeps failing, email ${COMPANY.privacyEmail}.`,
      );
      return;
    }
    // Before signing out: sign-out fires onAuthStateChange, and without `done`
    // already set the sign-in link would render in place of the confirmation.
    setScheduledFor(typeof data === "string" ? data : null);
    setDone(true);
    await supabase.auth.signOut();
    setBusy(false);
  }

  if (session === undefined) return null;

  if (done) {
    return (
      <div className="card" role="status">
        <h3 style={{ marginTop: 0 }}>Deletion requested</h3>
        <p>
          Your request was recorded and you have been signed out. This account can no longer sign
          in. Your account and its data will be deleted from our live systems
          {scheduledFor
            ? <> by <strong>{new Date(scheduledFor).toLocaleDateString()}</strong></>
            : " within 30 days"}
          , except records we must keep by law.
        </p>
        <p className="muted">
          Changed your mind? Email <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>{" "}
          before then.
        </p>
      </div>
    );
  }

  if (session === null) {
    return (
      <p>
        <Link className="btn-link primary" to={signInWithNext("/data-deletion")}>
          Sign in to delete your account
        </Link>
      </p>
    );
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Request account deletion</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Signed in as <strong>{session.user.email}</strong>
        {active?.name && <> · workspace <strong>{active.name}</strong></>}
      </p>

      <label htmlFor="delete-reason">Reason (optional)</label>
      <textarea id="delete-reason" value={reason} maxLength={1000} disabled={busy}
                placeholder="Tell us why you are leaving"
                onChange={(e) => setReason(e.target.value)} />

      <p>
        <label className="inline-check">
          <input type="checkbox" checked={confirmed} disabled={busy}
                 onChange={(e) => setConfirmed(e.target.checked)} />
          <span>I understand this permanently removes my account, posts, media and connections.</span>
        </label>
      </p>

      {/* A div, not a p: `.legal-body p` sets its own colour and outranks `.err`. */}
      {error && <div className="err" role="alert" style={{ margin: "0 0 .85rem" }}>{error}</div>}

      <button className={`danger${busy ? " is-busy" : ""}`}
              disabled={!confirmed || busy}
              onClick={() => void submit()}>
        {busy && <Spinner />}
        {busy ? "Submitting…" : "Delete my account"}
      </button>
    </div>
  );
}
